<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters } from "vuex"
import AlarmNotificationToggle from "@/components/alarms/notifications/AlarmNotificationToggle.vue"
import {
    AlarmNotificationHeaderTarget,
    AlarmNotificationOwnRow,
    AlarmNotificationScopes,
    AlarmNotificationState,
    nodeIcon,
    nodeKey,
    nodeState,
} from "@/types/alarm-notification"

export default defineComponent({
    name: "AlarmNotificationsHeaderMenu",
    components: { AlarmNotificationToggle },
    computed: {
        ...mapGetters("alarmNotifications", {
            effective: "effective",
            savingKeys: "savingKeys",
        }),
        ...mapGetters("location", {
            location: "location",
        }),
        target(): AlarmNotificationHeaderTarget | null {
            const types = this.$route.matched.map((record) => record.meta.type)
            const { did, locationId, locationGroupId } = this.$route.params

            if (types.includes("device") && did) {
                const device = this.$store.getters["breadcrumbs/getCategorizedData"]("device", did)

                return {
                    scope: AlarmNotificationScopes.DEVICE,
                    id: Number(did),
                    name: device?.title ?? "",
                    icon: nodeIcon(AlarmNotificationScopes.DEVICE, device?.data?.category?.value),
                }
            }

            if (types.includes("location") && locationId) {
                return {
                    scope: AlarmNotificationScopes.LOCATION,
                    id: Number(locationId),
                    name: this.location?.id === Number(locationId) ? this.location.name : "",
                    icon: nodeIcon(AlarmNotificationScopes.LOCATION),
                }
            }

            if (types.includes("location-group") && locationGroupId) {
                const group = this.$store.getters["breadcrumbs/getCategorizedData"](
                    "location-group",
                    locationGroupId,
                )

                return {
                    scope: AlarmNotificationScopes.LOCATION_GROUP,
                    id: Number(locationGroupId),
                    name: group?.title ?? "",
                    icon: nodeIcon(AlarmNotificationScopes.LOCATION_GROUP),
                }
            }

            return null
        },
        manageAllQuery(): Record<string, string> {
            const { companyId } = this.$route.params

            return this.$permissions.hasSuperAdminRole() && companyId
                ? { focus: this.targetKey, companyId }
                : { focus: this.targetKey }
        },
        targetKey(): string {
            return this.target ? nodeKey(this.target.scope, this.target.id) : ""
        },
        targetName(): string {
            return (
                this.target?.name ||
                this.$t(`alarm_notifications.scope.${this.target?.scope}`).toString()
            )
        },
        own(): AlarmNotificationOwnRow | null {
            const source = this.effective?.source

            return source && "subscriptionId" in source && !this.effective.inherited
                ? { subscriptionId: source.subscriptionId, type: source.type }
                : null
        },
        state(): AlarmNotificationState {
            return nodeState(this.own)
        },
        outcome(): string {
            return this.$t(
                this.effective?.subscribed ? "alarm_notifications.on" : "alarm_notifications.off",
            ).toString()
        },
        isSaving(): boolean {
            return !!this.targetKey && this.savingKeys.includes(this.targetKey)
        },
    },
    watch: {
        targetKey: {
            immediate: true,
            handler(targetKey: string) {
                this.$store.commit("alarmNotifications/setEffective", null)

                if (targetKey) this.fetchEffective()
            },
        },
    },
    methods: {
        fetchEffective() {
            return this.$store.dispatch("alarmNotifications/fetchEffective", {
                scope: this.target?.scope,
                id: this.target?.id,
            })
        },
        async select(state: AlarmNotificationState) {
            if (!this.target) return

            const saved = await this.$store.dispatch("alarmNotifications/saveNotification", {
                scope: this.target.scope,
                id: this.target.id,
                own: this.own,
                state,
            })

            if (saved) {
                this.$store.dispatch("toastMessageModule/showMessage", {
                    message: this.$t(`alarm_notifications.changed.${state.toLowerCase()}`, {
                        name: this.targetName,
                    }).toString(),
                    color: "green",
                })
            }

            this.fetchEffective()
        },
    },
})
</script>

<template>
    <b-dropdown
        v-if="target && effective"
        right
        size="sm"
        variant="primary"
        class="alarm-notifications-menu"
        menu-class="menu"
    >
        <template #button-content>
            <span
                class="fa-solid mr-1"
                :class="effective.subscribed ? 'fa-bell' : 'fa-bell-slash'"
            ></span>
            <span class="d-none d-md-inline">{{ $t("alarm_notifications.title") }}:</span>
            {{ outcome }}
        </template>

        <li role="presentation" class="menu-body">
            <div class="d-flex align-items-start mb-2">
                <span class="fa-solid fa-fw target-icon mr-2" :class="target.icon"></span>
                <div class="target-text">
                    <div class="text-primary">{{ targetName }}</div>
                    <div v-if="state === 'DEFAULT'" class="text-aux small">{{ outcome }}</div>
                </div>
            </div>
            <div class="d-flex align-items-center">
                <AlarmNotificationToggle
                    class="flex-grow-1"
                    :value="state"
                    :disabled="isSaving"
                    block
                    @input="select"
                />
                <b-spinner v-if="isSaving" small class="saving-spinner ml-2" />
            </div>
            <div class="text-right mt-2">
                <router-link
                    :to="{ name: 'UserAlarmNotifications', query: manageAllQuery }"
                    class="btn btn-link btn-sm link p-0"
                >
                    {{ $t("alarm_notifications.manage_all") }}
                    <span class="fa-solid fa-chevron-right ml-1"></span>
                </router-link>
            </div>
        </li>
    </b-dropdown>
</template>

<style scoped lang="scss">
.alarm-notifications-menu ::v-deep .menu {
    min-width: 16rem;
    background-color: var(--theme-primary);
    border: 1px solid var(--theme-tertiary);
}

.menu-body {
    padding: 0.5rem 0.75rem;
}

.target-icon {
    margin-top: 0.2rem;
    color: var(--theme-shade);
}

.target-text {
    min-width: 0;
    overflow-wrap: anywhere;
}

.text-aux {
    color: var(--theme-auxiliary);
}

.link {
    color: var(--theme-shade);
}

.saving-spinner {
    color: var(--theme-shade);
}
</style>
